import * as React from 'react';

import './plans-carousel.scss';
import { getSwipeDirection, moveToSlide, outerWidth } from './plans-carousel-utils';

export function getElementWidth(elementCssSelector) {
  return elementCssSelector ? outerWidth(document.querySelector(elementCssSelector)) : 0;
}

const SWIPE_THRESHOLD = 44;

interface IPlansCarousel {
  children: React.ReactNode;
  columnCount: number;
  columnCssSelector?: string;
  onSlideComplete?: () => {};
  // columnCssSelector: (props, propName, componentName) => {
  //   if (typeof props[propName] !== 'string' || !props[propName].length) {
  //     return new Error(
  //       `Invalid prop "${propName}" supplied to "${componentName}". Expecting valid string.`,
  //     );
  //   }
  // };
}

interface IPlansCarouselState {
  navItems: string[];
  xPosition: number;
}

export default class PlansCarousel extends React.Component<IPlansCarousel, IPlansCarouselState> {
  static defaultProps = {
    onSlideComplete: () => {},
    columnCssSelector: () => {},
  };

  constructor(props: IPlansCarousel) {
    super(props);
    this.columnNode = null;
    this.rootNode = null;
    this.frameNode = null;
    this.state = {
      navItems: [],
      xPosition: 0,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowResize);
    this.resetCarouselProperties();
    this.bootstrapInteractiveElements();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  /*
   * 1. determine col with
   * 2. work out how many cols the window size allows
   * 3. work out total slides based on total col count minus visible cols: this gives the total number of
   * slides based on a 1 x colWidth slide amount to cover every col. We add 1 for the current, visible slide.
   */
  setSlideProperties() {
    this.childColumnWidth = getElementWidth(this.props.columnCssSelector) || this.childColumnWidth;
    this.visibleCols =
      Math.floor((window.innerWidth - this.rootNode.offsetLeft) / this.childColumnWidth) || 1;
    this.slideCount = this.props.columnCount - this.visibleCols + 1;
  }

  /*
   * calculate how many cols are within the active slide to make the nav dots active
   * the end range is determined by how many visible slides there are after the currentIndex
   */
  setNavItems() {
    if (!this.allowInteraction || this.visibleCols === this.props.columnCount) {
      return;
    }
    const columnIndexes = [...Array(this.props.columnCount).keys()];
    const activeIndexes = columnIndexes.slice(
      this.currentSlideIndex,
      this.currentSlideIndex + this.visibleCols,
    );
    const navItems = columnIndexes.map((item) => {
      return activeIndexes.includes(item) ? 1 : 0;
    });
    this.setState({
      navItems,
    });
  }

  /*
   * keep track of whether we need to show the dots and allow other interaction
   */
  setAllowInteraction = () => {
    this.allowInteraction =
      this.props.columnCount > 0 && window.innerWidth < this.frameNode.scrollWidth;
  };

  /*
   * React ref callback to get DOM node of carousel container and frame
   */
  setNodesCallback = (ref) => {
    if (ref) {
      this.rootNode = ref.parentNode;
      this.frameNode = ref;
    }
  };

  getTargetXPosition = (touchOffset) => {
    const xPosition = this.currentXPosition + touchOffset * -1;
    return xPosition >= 0 ? 0 : xPosition;
  };

  handleOnGoToSlideCallback = () => {
    if (this.animating === true) {
      this.setNavItems();
      this.setState(
        {
          xPosition: this.currentXPosition,
        },
        () => {
          this.props.onSlideComplete({
            currentSlideIndex: this.currentSlideIndex,
            columnCount: this.props.columnCount,
            slideCount: this.slideCount,
          });
        },
      );
    }
    this.animating = false;
    this.touchObject = {};
    this.dragging = false;
    this.disableClick = false;
  };

  /**
   * Bring slide to first position
   * @param {Number} newSlideIndex - index of slide we want to go to
   * @param {Boolean} shouldAnimate - whether we should animate there or not
   */
  goToSlide = (newSlideIndex, shouldAnimate) => {
    // don't accept slide index values out of range
    if (newSlideIndex > this.slideCount - 1 || newSlideIndex < 0) {
      return;
    }
    this.currentSlideIndex = newSlideIndex;
    const newPosition = -1 * this.childColumnWidth * this.currentSlideIndex;
    this.animating = true;
    moveToSlide(
      this.frameNode,
      newPosition,
      () => {
        this.currentXPosition = newPosition;
        this.handleOnGoToSlideCallback();
      },
      shouldAnimate,
    );
  };

  nextSlide = () => {
    if (this.currentSlideIndex > this.slideCount - 1) {
      return;
    }
    const newSlideIndex =
      this.currentSlideIndex < this.slideCount - 1
        ? this.currentSlideIndex + 1
        : this.currentSlideIndex;
    this.goToSlide(newSlideIndex, true);
  };

  previousSlide = () => {
    if (this.currentSlideIndex <= 0) {
      return;
    }
    const newSlideIndex =
      this.currentSlideIndex > 0 ? this.currentSlideIndex - 1 : this.currentSlideIndex;
    this.goToSlide(newSlideIndex, true);
  };

  handleWindowResize = () => {
    this.setAllowInteraction();
    if (!this.allowInteraction) {
      this.resetCarouselProperties();
    } else {
      this.setSlideProperties();
      this.setNavItems();
      if (this.currentSlideIndex > this.slideCount - 1) {
        this.goToSlide(this.currentSlideIndex - 1);
      }
    }
  };

  /*
   * controls how clicks are propagated during dragging
   */
  handleClick = (event) => {
    if (this.disableClick === true) {
      event.preventDefault();
      event.stopPropagation();
      if (event.nativeEvent) {
        event.nativeEvent.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
      }
    }
    this.disableClick = false;
  };

  /*
   * detects valid swipe, then goes to slide
   */
  handleSwipe = () => {
    if (
      (typeof this.touchObject.moveLength !== 'undefined' && this.touchObject.moveLength === 0) ||
      this.currentXPosition == this.state.xPosition
    ) {
      this.handleOnGoToSlideCallback();
      return;
    }

    if (
      typeof this.touchObject.moveLength !== 'undefined' &&
      this.touchObject.moveLength <= SWIPE_THRESHOLD
    ) {
      this.goToSlide(this.currentSlideIndex, false);
      return;
    }

    if (
      this.state.xPosition <= this.rootNode.offsetLeft &&
      // TODO: look at this as a possible abstraction
      // if the value of x position of the frame is greater than the width of the off-screen columns
      -1 * (this.state.xPosition <= this.childColumnWidth * (this.props.columnCount - 1))
    ) {
      this.disableClick = true;
      if (this.touchObject.direction === 1) {
        this.nextSlide();
      }
      if (this.touchObject.direction === -1) {
        this.previousSlide();
      }
    } else {
      this.goToSlide(this.currentSlideIndex, true);
    }
  };

  resetCarouselProperties = () => {
    this.setState({
      navItems: [],
      xPosition: 0,
    });
    this.currentSlideIndex = 0;
    this.currentXPosition = 0;
    this.touchObject = {};
    this.dragging = false;
    this.animating = false;
    this.disableClick = false;
    this.slideCount = 1;
    this.visibleCols = 1;
    this.childColumnWidth = 0;
    this.allowInteraction = false;
  };

  bootstrapInteractiveElements = () => {
    this.columnNode = document.querySelector(this.props.columnCssSelector);
    this.setAllowInteraction();
    this.setSlideProperties();
    this.setNavItems();
  };

  /*
   * Click callback for nav dots
   * Args: selectedIndex - index of the column (not the slide) to go to
   */
  handleNavigationClick = (selectedIndex) => {
    if (!this.animating) {
      this.goToSlide(
        // if we're not dealing with an equal amount of cols and slides and
        // and the desired col index is larger than the number of slides
        this.slideCount !== this.props.columnCount && selectedIndex > this.slideCount - 1
          ? // remove from the selectedIndex the diff between selectedIndex and the last slide index
            // to get the next permissible slide index
            selectedIndex - (selectedIndex - (this.slideCount - 1))
          : selectedIndex,
        true,
      );
    }
  };

  frameStyles = () => {
    if (this.state) {
      return {
        left: `${this.state.xPosition}px`,
      };
    }
  };

  touchEventHandlers = () => {
    const _this = this;
    return {
      onTouchStart(event) {
        _this.touchObject = {
          startX: event.touches[0].pageX,
          startY: event.touches[0].pageY,
          moveLength: 0,
        };
      },
      onTouchMove(event) {
        if (!_this.allowInteraction) {
          return;
        }
        const direction = getSwipeDirection(
          _this.touchObject.startX,
          event.touches[0].pageX,
          _this.touchObject.startY,
          event.touches[0].pageY,
        );
        // chrome's changes to passive events:
        // https://github.com/facebook/react/issues/6436
        if ((direction === 1 || direction === -1) && event.preventDefault) {
          event.preventDefault();
        }
        const moveLength = Math.round(
          Math.sqrt(Math.pow(event.touches[0].pageX - _this.touchObject.startX, 2)),
        );
        _this.touchObject = {
          startX: _this.touchObject.startX,
          startY: _this.touchObject.startY,
          endX: event.touches[0].pageX,
          endY: event.touches[0].pageY,
          direction,
          moveLength,
        };
        _this.setState({
          xPosition: _this.getTargetXPosition(moveLength * _this.touchObject.direction),
        });
      },
      onTouchEnd(event) {
        _this.handleSwipe(event);
      },
      onTouchCancel(event) {
        _this.handleSwipe(event);
      },
    };
  };

  mouseEventHandlers = () => {
    const _this = this;
    return {
      onMouseDown(event) {
        _this.touchObject = {
          startX: event.clientX,
          startY: event.clientY,
          moveLength: 0,
        };
        _this.dragging = true;
      },
      onMouseMove(event) {
        if (!_this.allowInteraction) {
          return;
        }
        if (!_this.dragging) {
          return;
        }
        const direction = getSwipeDirection(
          _this.touchObject.startX,
          event.clientX,
          _this.touchObject.startY,
          event.clientY,
        );
        if (direction !== 0) {
          event.preventDefault();
        }
        const moveLength = Math.round(
          Math.sqrt(Math.pow(event.clientX - _this.touchObject.startX, 2)),
        );
        _this.touchObject = {
          startX: _this.touchObject.startX,
          startY: _this.touchObject.startY,
          endX: event.clientX,
          endY: event.clientY,
          direction,
          moveLength,
        };
        _this.setState({
          xPosition: _this.getTargetXPosition(moveLength * _this.touchObject.direction),
        });
      },
      onMouseUp(event) {
        if (!_this.dragging) {
          return;
        }
        _this.handleSwipe(event);
      },
      onMouseLeave(event) {
        if (!_this.dragging) {
          return;
        }
        _this.handleSwipe(event);
      },
    };
  };

  render() {
    const { children } = this.props;
    return (
      <div className="plans-carousel" onClickCapture={this.handleClick}>
        <div
          className="plans-carousel__container"
          {...this.mouseEventHandlers()}
          {...this.touchEventHandlers()}
        >
          <div
            className="plans-carousel__frame"
            ref={this.setNodesCallback}
            style={this.frameStyles()}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
}
